import { useState, useEffect, useCallback, useRef, createContext, useContext, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { getPriority } from '../lib/priority'
import { getAssignmentType } from '../lib/assignmentType'
import { generateTaskId } from '../lib/helpers'
import { useAuth } from './useAuth'
import { playTaskSound } from '../lib/notificationSounds'
import { onMessage } from '../lib/dmEventBus'
import { buildSubtaskCounts } from '../lib/subtasks'

// Sender-side realtime "poke": nudge an assignee's task list to refetch
// immediately, bypassing any RLS/filter edge cases on postgres_changes for
// the tasks + task_assignees tables. Each assignee's useTasks hook
// subscribes to user:{theirId}:task-poke; this function sends one
// broadcast on that channel and tears the channel down.
//
// `ack: true` makes ch.send() resolve only when the realtime server has
// acknowledged delivery, which means we can safely tear the channel down
// right after. Without ack, send() returns immediately and a fast
// removeChannel() can race the underlying socket flush — receivers see
// nothing.
async function pokeAssignee(userId) {
  const topic = `user:${userId}:task-poke`
  const ch = supabase.channel(topic, {
    config: { broadcast: { self: false, ack: true } },
  })
  let subscribed = false
  await new Promise((resolve) => {
    ch.subscribe((status) => {
      if (status === 'SUBSCRIBED') { subscribed = true; resolve() }
    })
    // Safety timeout so we don't hang if the socket is down.
    setTimeout(resolve, 1500)
  })
  if (!subscribed) {
    if (typeof window !== 'undefined' && window.__pe_debug) {
      console.log('[pe-debug] pokeAssignee: subscribe timed out, send may be lost', { userId })
    }
    try { supabase.removeChannel(ch) } catch { /* noop */ }
    return
  }
  try {
    const result = await ch.send({ type: 'broadcast', event: 'task-changed', payload: {} })
    if (typeof window !== 'undefined' && window.__pe_debug) {
      console.log('[pe-debug] pokeAssignee sent', { userId, topic, result })
    }
  } finally {
    // ack: true means send resolved only after server flush; safe to
    // tear down immediately, but a tiny grace beat is harmless.
    setTimeout(() => { try { supabase.removeChannel(ch) } catch { /* noop */ } }, 50)
  }
}

// Lean read-time SELECT. Heavy nested embeds (profile_teams, nested team data,
// per-row comment count, attachment count) were dropped after audit:
//   • attachment_count had zero consumers in the app.
//   • comment_count is now fetched via get_user_task_comment_counts() RPC
//     (one aggregation, not N LATERAL subqueries).
//   • assignee.team_ids / team_roles / all_teams aren't read off task.assignee
//     anywhere in the app — those fields are only used off useAuth().profile
//     or useProfiles(). Pulling them per task was pure waste.
// reports_to stays on assignee because the manager chip uses it for lookup.
const TASK_SELECT_FULL = `
  *,
  assignee:profiles!tasks_assigned_to_fkey(id, full_name, avatar_url, reports_to),
  assigner:profiles!tasks_assigned_by_fkey(id, full_name),
  task_assignees!task_assignees_task_id_fkey(profile_id, is_primary, completed_at, completed_by, profile:profiles!task_assignees_profile_id_fkey(id, full_name, avatar_url)),
  team:teams(id, name)
`

const TASK_SELECT_FALLBACK = `
  *,
  assignee:profiles!tasks_assigned_to_fkey(id, full_name, avatar_url, reports_to),
  assigner:profiles!tasks_assigned_by_fkey(id, full_name),
  team:teams(id, name)
`

// PostgREST caps EVERY response (tables and set-returning RPCs) at 1000 rows.
// Past ~1000 visible tasks the unpaged SELECT silently dropped the oldest rows
// — projects whose features predate the cutoff showed 0 features / 0% on
// /projects. Page with .range() until a short page signals the end. buildQuery
// must include a deterministic ORDER BY (ties across page boundaries skip or
// duplicate rows otherwise).
const PAGE_SIZE = 1000
async function fetchAllPages(buildQuery) {
  let all = []
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await buildQuery().range(from, from + PAGE_SIZE - 1)
    if (error) return { data: all, error }
    all = all.concat(data || [])
    if (!data || data.length < PAGE_SIZE) return { data: all, error: null }
  }
}

// Shared task state. Mounted once via <TasksProvider>; every useTasks() call
// reads from this context instead of spawning its own fetch/realtime channel.
// Before: NotificationBell + MyTasksPage each ran the full enrichment chain
// (tasks SELECT + comment_counts RPC + chat_unreads RPC + assignees lookup
// + manager lookup) on initial load AND on every realtime tick. The cold-load
// fetch volume was ~76 round-trips on a fresh page; lifting to a singleton
// halves that without changing the hook's public shape.
const TasksContext = createContext(null)

function useTasksImpl() {
  const { profile, isAdmin, isManager } = useAuth()
  const [tasks,   setTasks]   = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)

  // Refs keep fetchTasks stable so the realtime subscription doesn't tear down/re-subscribe
  const profileRef = useRef(profile)
  const isAdminRef = useRef(isAdmin)
  const isManagerRef = useRef(isManager)
  useEffect(() => {
    profileRef.current = profile
    isAdminRef.current = isAdmin
    isManagerRef.current = isManager
  }, [profile, isAdmin, isManager])

  // Mirror the current tasks array so realtime handlers can answer
  // "is this row in my list?" without re-subscribing on every change.
  const tasksRef = useRef([])
  useEffect(() => { tasksRef.current = tasks }, [tasks])

  const fetchTasks = useCallback(async (silent = false) => {
    if (!profileRef.current) return
    if (!silent) setLoading(true)
    setError(null)

    // Run the main task query and the comment-count aggregation in parallel.
    // Comment counts used to be a per-row LATERAL subquery via PostgREST's
    // `comments(count)` embed — fine for a few rows, brutal for 100+.
    let usedFallback = false
    const [tasksRes, commentCountsRes] = await Promise.all([
      fetchAllPages(() => supabase.from('tasks').select(TASK_SELECT_FULL)
        .order('date_assigned', { ascending: false }).order('id', { ascending: true })),
      // The RPC returns one row per visible task (LEFT JOIN) — same 1000-row
      // cap, so paginate it too. RPC row order isn't guaranteed; order by
      // task_id for stable pages.
      fetchAllPages(() => supabase.rpc('get_user_task_comment_counts')
        .order('task_id', { ascending: true })),
    ])
    let { data, error } = tasksRes
    if (error) {
      console.warn('task_assignees join failed, using fallback query:', error.message)
      usedFallback = true
      const retry = await fetchAllPages(() => supabase.from('tasks').select(TASK_SELECT_FALLBACK)
        .order('date_assigned', { ascending: false }).order('id', { ascending: true }))
      data = retry.data
      error = retry.error
    }
    if (error) { setError(error.message); setLoading(false); return }

    const commentCountByTaskId = new Map()
    if (commentCountsRes?.error) {
      console.warn('get_user_task_comment_counts failed:', commentCountsRes.error.message)
    } else if (commentCountsRes?.data) {
      for (const r of commentCountsRes.data) {
        commentCountByTaskId.set(r.task_id, Number(r.comment_count) || 0)
      }
    }

    // If fallback was used, fetch task_assignees separately
    let assigneesMap = {}
    if (usedFallback && data?.length) {
      const { data: taData } = await fetchAllPages(() => supabase
        .from('task_assignees')
        .select('task_id, profile_id, is_primary, completed_at, completed_by, profile:profiles(id, full_name, avatar_url)')
        .order('task_id', { ascending: true }).order('profile_id', { ascending: true }))
      if (taData) {
        for (const ta of taData) {
          if (!assigneesMap[ta.task_id]) assigneesMap[ta.task_id] = []
          assigneesMap[ta.task_id].push(ta)
        }
      }
    }

    // Resolve reporting manager names for assignees
    const managerIds = [...new Set((data || []).map(t => t.assignee?.reports_to).filter(Boolean))]
    let managerMap = {}
    if (managerIds.length > 0) {
      const { data: managers } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', managerIds)
      if (managers) {
        managerMap = Object.fromEntries(managers.map(m => [m.id, m]))
      }
    }

    // Unread task-chat counts for the current user. One RPC call instead
    // of N+1 HEAD count queries (migration 052).
    const taskIds = (data || []).map(t => t.id).filter(Boolean)
    const unreadByTaskId = new Map()
    if (taskIds.length > 0) {
      const { data: unreadRows, error: unreadErr } = await fetchAllPages(() =>
        supabase.rpc('get_user_task_chat_unreads', { p_task_ids: taskIds })
          .order('task_id', { ascending: true }))
      if (unreadErr) {
        console.warn('get_user_task_chat_unreads failed:', unreadErr.message)
      } else if (unreadRows) {
        for (const r of unreadRows) {
          unreadByTaskId.set(r.task_id, Number(r.unread_count) || 0)
        }
      }
    }

    // Personal archive set (migration 105). task_archives is per-user; RLS
    // already scopes rows to the caller, so no user_id filter is needed. Tag
    // tasks below so active lists can drop archived rows and the Archived tab
    // can keep them. A failure here degrades gracefully (set stays empty →
    // nothing looks archived) rather than breaking the whole task fetch.
    const archivedSet = new Set()
    {
      const { data: archRows, error: archErr } = await supabase
        .from('task_archives')
        .select('task_id')
      if (archErr) console.warn('task_archives fetch failed:', archErr.message)
      else for (const r of archRows || []) archivedSet.add(r.task_id)
    }

    const subtaskCounts = buildSubtaskCounts(data || [])

    const enriched = (data || []).map(t => {
      // Build assignees array from junction table (inline join or fallback map)
      const rawAssignees = t.task_assignees?.length ? t.task_assignees : (assigneesMap[t.id] || [])
      const assignees = rawAssignees.map(ta => ({
        id: ta.profile?.id || ta.profile_id,
        full_name: ta.profile?.full_name,
        avatar_url: ta.profile?.avatar_url,
        is_primary: ta.is_primary,
        completed_at: ta.completed_at ?? null,
        completed_by: ta.completed_by ?? null,
      }))

      const counts = subtaskCounts.get(t.id) || { total: 0, open: 0 }
      return {
        ...t,
        priority:      getPriority(t),
        comment_count: commentCountByTaskId.get(t.id) || 0,
        unread_chat_count: unreadByTaskId.get(t.id) || 0,
        subtask_count:      counts.total,
        open_subtask_count: counts.open,
        assignee:      t.assignee ? { ...t.assignee, manager: managerMap[t.assignee.reports_to] || null } : t.assignee,
        assignees,
        archived:      archivedSet.has(t.id),
      }
    })

    // Surgically merge new data with current state so unchanged rows keep
    // their previous object identity. fetchTasks always rebuilds enriched
    // task objects fresh, so naive `setTasks(enriched)` gives every task —
    // even ones whose data didn't change — a new identity, and React then
    // re-renders every TaskTable row. With 100+ tasks open on the admin
    // dashboard, that's the visible "refresh" the user reported on tab
    // return when even a single task elsewhere in the org got an update.
    //
    // Strategy: build the result by walking enriched in order, and for
    // each row, compare against the same id in prev. If they're equal on
    // every field that drives the visible UI, reuse prev's object. If
    // anything visible changed, take the new object. If lengths and
    // every reference still match the previous array, return prev itself
    // (no state update at all → no re-render).
    setTasks(prev => {
      const prevById = new Map(prev.map(t => [t.id, t]))
      let differs = enriched.length !== prev.length
      const next = enriched.map((b, i) => {
        const a = prevById.get(b.id)
        if (!a) { differs = true; return b }
        const sameMeta = (
          a.last_updated === b.last_updated &&
          a.status === b.status &&
          a.urgency === b.urgency &&
          a.due_date === b.due_date &&
          a.title === b.title &&
          a.notes === b.notes &&
          a.icon === b.icon &&
          a.acceptance_status === b.acceptance_status &&
          a.assigned_to === b.assigned_to &&
          a.assigned_by === b.assigned_by &&
          a.team_id === b.team_id &&
          a.priority === b.priority &&
          a.archived === b.archived &&
          (a.comment_count || 0) === (b.comment_count || 0) &&
          (a.unread_chat_count || 0) === (b.unread_chat_count || 0) &&
          (a.subtask_count || 0) === (b.subtask_count || 0) &&
          (a.open_subtask_count || 0) === (b.open_subtask_count || 0) &&
          (a.assignees?.length || 0) === (b.assignees?.length || 0)
        )
        let sameAssignees = sameMeta
        if (sameMeta && a.assignees && b.assignees) {
          for (let j = 0; j < a.assignees.length; j++) {
            if (
              a.assignees[j].id !== b.assignees[j].id ||
              a.assignees[j].completed_at !== b.assignees[j].completed_at
            ) { sameAssignees = false; break }
          }
        }
        // Position in the array also matters — same id at a different
        // index means the row order changed, which IS visible. Force a
        // new array in that case even if the row itself is unchanged.
        const samePosition = prev[i]?.id === a.id
        if (sameAssignees && samePosition) return a
        differs = true
        return b
      })
      if (!differs) {
        if (typeof window !== 'undefined' && window.__pe_debug) {
          console.log('[pe-debug] setTasks NO-OP (every row unchanged)')
        }
        return prev
      }
      if (typeof window !== 'undefined' && window.__pe_debug) {
        const changedCount = next.reduce((acc, t, i) => acc + (t === prev[i] ? 0 : 1), 0)
        console.log('[pe-debug] setTasks', enriched.length, 'rows ·', changedCount, 'changed · silent=', silent)
      }
      return next
    })
    setLoading(false)
  }, []) // profile/isAdmin/isManager accessed via refs to keep identity stable

  // Initial fetch — re-run only when profile arrives (not on every reference change)
  const profileId = profile?.id
  useEffect(() => { fetchTasks() }, [profileId, fetchTasks])

  // Real-time subscriptions.
  //
  // Two channels are needed:
  //   1. tasks — primary assignees (assigned_to = me) or tasks I created see
  //      INSERT/UPDATE/DELETE directly. Their RLS SELECT check passes on
  //      those columns at the moment the row is written.
  //   2. task_assignees — **secondary** assignees (everyone past the first in
  //      a multi-owner assignment) can't see the tasks INSERT yet: the
  //      junction row that grants them RLS access is written in a second
  //      insert right after. So we listen to the junction and refetch when
  //      a row mentions me.
  //
  // Without (2), the recipient of a multi-owner assignment had to refresh
  // the page before the new task appeared — which is what users were
  // reporting.
  //
  // Refetches are coalesced through scheduleRefetch() so a burst of events
  // from one logical action (e.g. assigning a task with 4 assignees writes
  // 1 tasks row + 4 task_assignees rows in milliseconds) collapses to a
  // single fetch. Events that don't affect the current user's visible task
  // list are dropped entirely — that's the fix for the "page auto-refreshes
  // whenever something changes elsewhere" UX bug.
  useEffect(() => {
    if (!profileId) return

    let refetchTimer = null
    function scheduleRefetch(reason) {
      if (typeof window !== 'undefined' && window.__pe_debug) {
        console.log('[pe-debug] scheduleRefetch', { reason, alreadyScheduled: !!refetchTimer })
      }
      if (refetchTimer) return
      refetchTimer = setTimeout(() => {
        refetchTimer = null
        fetchTasks(true)
      }, 250)
    }

    // Reconnect-aware status tracking, per-channel. First SUBSCRIBED is the
    // initial connect (no refetch — we've just fetched). Each subsequent
    // transition from a non-SUBSCRIBED state back to SUBSCRIBED means we
    // reconnected, and we refetch to catch anything missed during the
    // outage. Each channel needs its own state since they reconnect
    // independently — previously only `tasksChannel` carried this, so a
    // silent drop on the assignees or poke channel left us blind until the
    // next user-driven refetch.
    function makeStatusHandler(label) {
      let everConnected = false
      return (status) => {
        if (typeof window !== 'undefined' && window.__pe_debug) {
          console.log('[pe-debug] channel status', { channel: label, status })
        }
        if (status === 'SUBSCRIBED') {
          if (everConnected) scheduleRefetch(`reconnect:${label}`)
          everConnected = true
        } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          everConnected = false
        }
      }
    }

    // Tasks channel — primary assignees / task creators / managers see events
    // directly through RLS. Even with RLS, an admin/manager sees changes for
    // tasks they don't actively care about (e.g. another team member's notes
    // tweak). Filter to "involves me OR already in my list" so unrelated
    // backend churn doesn't redraw the page.
    const tasksChannel = supabase
      .channel(`tasks-realtime:${profileId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' },
        (payload) => {
          const newRow = payload.new
          const oldRow = payload.old
          const row = newRow || oldRow
          if (!row) return

          const involvesMe =
            newRow?.assigned_to === profileId || newRow?.assigned_by === profileId ||
            oldRow?.assigned_to === profileId || oldRow?.assigned_by === profileId
          const inMyList = tasksRef.current.some(t => t.id === row.id)
          if (typeof window !== 'undefined' && window.__pe_debug) {
            console.log('[pe-debug] tasks postgres_changes', {
              eventType: payload.eventType,
              taskId: row.id,
              assigned_to: newRow?.assigned_to,
              assigned_by: newRow?.assigned_by,
              involvesMe, inMyList,
            })
          }
          if (!involvesMe && !inMyList) return

          if (payload.eventType === 'INSERT' && newRow?.assigned_to === profileId && newRow?.assigned_by !== profileId) {
            playTaskSound()
          }
          scheduleRefetch('tasks-postgres-changes')
        })
      .subscribe(makeStatusHandler('tasks'))

    // Task-assignees channel — secondary assignees land here (tasks INSERT is
    // suppressed by RLS until their junction row exists).
    //
    // Deliberately UNFILTERED on profile_id at the subscription level —
    // server-side realtime filters on composite-PK tables are known to drop
    // events under some conditions. We filter client-side instead, where the
    // logic is "row references me OR row references a task I can already
    // see". The earlier version refetched on every event and was the loudest
    // source of the spurious-refresh bug.
    const assigneesChannel = supabase
      .channel(`task-assignees-realtime:${profileId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'task_assignees' },
        (payload) => {
          const newRow = payload.new
          const oldRow = payload.old
          const row = newRow || oldRow
          if (!row) return

          const involvesMe = newRow?.profile_id === profileId || oldRow?.profile_id === profileId
          const inMyList = tasksRef.current.some(t => t.id === row.task_id)
          if (typeof window !== 'undefined' && window.__pe_debug) {
            console.log('[pe-debug] task_assignees postgres_changes', {
              eventType: payload.eventType,
              taskId: row.task_id,
              profile_id: newRow?.profile_id,
              is_primary: newRow?.is_primary,
              involvesMe, inMyList,
            })
          }
          if (!involvesMe && !inMyList) return

          // Sound only on NEW assignments to the current user.
          if (payload.eventType === 'INSERT' && !row.is_primary && row.profile_id === profileId) {
            playTaskSound()
          }
          scheduleRefetch('assignees-postgres-changes')
        }
      )
      .subscribe(makeStatusHandler('assignees'))

    // User-scoped broadcast channel — the sender-side "poke" from
    // assignTask fires here. This is RLS-free and filter-free, so it's the
    // single most reliable signal we have. If WebSocket is up at all, this
    // path works.
    const pokeChannel = supabase
      .channel(`user:${profileId}:task-poke`, { config: { broadcast: { self: false } } })
      .on('broadcast', { event: 'task-changed' }, () => {
        if (typeof window !== 'undefined' && window.__pe_debug) {
          console.log('[pe-debug] poke broadcast received')
        }
        playTaskSound()
        scheduleRefetch('poke-broadcast')
      })
      .subscribe(makeStatusHandler('poke'))

    return () => {
      if (refetchTimer) { clearTimeout(refetchTimer); refetchTimer = null }
      supabase.removeChannel(tasksChannel)
      supabase.removeChannel(assigneesChannel)
      supabase.removeChannel(pokeChannel)
    }
  }, [profileId, fetchTasks])

  // (Tab-visible refetch removed.) Browsers don't always tear down the
  // websocket on background tabs, so a "silent" refetch on every visibility
  // flip mostly does nothing — but it ALWAYS swaps the `tasks` array and
  // every task object identity, which propagates as a new prop into
  // TaskDetailPanel and causes a perceptible re-render burst on tab return.
  // Missed events are already covered by the reconnect-aware refetch above
  // (fires only when the realtime channel actually went down and came back),
  // and by the user-scoped task-poke broadcast for new assignments.

  // Refresh unread_chat_count when DM messages arrive. The global
  // useDmRealtime subscription fires dmEventBus "message" events for any
  // conversation the user participates in — including task chats. We only
  // need the unread counts here, so run the unread-counts RPC alone and
  // patch tasks in place; full fetchTasks would re-render every row for a
  // single-column update. Debounced to coalesce paste-multiple bursts.
  useEffect(() => {
    if (!profileId) return
    let timer = null
    const unsub = onMessage(() => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(async () => {
        const ids = tasksRef.current.map(t => t.id).filter(Boolean)
        if (ids.length === 0) return
        const { data: rows, error: err } = await supabase
          .rpc('get_user_task_chat_unreads', { p_task_ids: ids })
        if (err) {
          console.warn('get_user_task_chat_unreads (dm refresh) failed:', err.message)
          return
        }
        const unreadByTaskId = new Map()
        for (const r of rows || []) {
          unreadByTaskId.set(r.task_id, Number(r.unread_count) || 0)
        }
        // Patch only the unread_chat_count column. Bail out with the
        // existing array reference if no row's count actually changed, so
        // unrelated subscribers don't see a state-update notification.
        setTasks(prev => {
          let changed = false
          const next = prev.map(t => {
            const newCount = unreadByTaskId.get(t.id) || 0
            if ((t.unread_chat_count || 0) === newCount) return t
            changed = true
            return { ...t, unread_chat_count: newCount }
          })
          return changed ? next : prev
        })
      }, 500)
    })
    return () => { if (timer) clearTimeout(timer); unsub() }
  }, [profileId])

  // My tasks only (primary + secondary assignee). Reads from the enriched
  // `assignees` array (line 184), which is populated either from the inline
  // join or from the fallback map. The legacy `task_assignees` field only
  // survives on the inline-join path, so we'd silently miss secondaries
  // whenever the join 401s and falls back.
  const myTasks = tasks.filter(t =>
    t.assigned_to === profile?.id ||
    t.assignees?.some(a => a.id === profile?.id)
  )

  // Team tasks (for manager view) — only teams where user has Manager role
  const teamTasks = isManager
    ? tasks.filter(t => {
        if (profile?.role === 'Admin') return true
        const teamRoles = profile?.team_roles || {}
        return teamRoles[t.team_id] === 'Manager'
      })
    : []

  return useMemo(
    () => ({ tasks, myTasks, teamTasks, loading, error, refetch: fetchTasks }),
    [tasks, myTasks, teamTasks, loading, error, fetchTasks]
  )
}

export function TasksProvider({ children }) {
  const value = useTasksImpl()
  return <TasksContext.Provider value={value}>{children}</TasksContext.Provider>
}

export function useTasks() {
  const ctx = useContext(TasksContext)
  if (!ctx) {
    // Fallback for callers mounted outside TasksProvider — degrades to
    // local-state behavior (matches the old API). Should never happen in
    // the deployed tree; this is just defensive.
    return { tasks: [], myTasks: [], teamTasks: [], loading: false, error: null, refetch: async () => {} }
  }
  return ctx
}

export function useTaskActions() {
  const { profile } = useAuth()

  async function assignTask(payload) {
    const { assigneeIds, assigneeId, title, urgency, dueDate, whoTo, notes, icon, allProfiles, overrideAssignerId, teamId, parentTaskId, projectId, projectColumnId, projectPos } = payload

    // Support both single assigneeId (legacy) and multiple assigneeIds
    const ids = assigneeIds?.length ? assigneeIds : [assigneeId]
    const primaryId = ids[0]
    const assignee = allProfiles.find(p => p.id === primaryId)
    const statedAssigner = overrideAssignerId
      ? allProfiles.find(p => p.id === overrideAssignerId)
      : profile
    const actualAssigner = profile

    const resolvedTeam = teamId || assignee?.team_id
    const assignmentType = getAssignmentType(statedAssigner, assignee, resolvedTeam)
    const taskId = generateTaskId()

    const { data, error } = await supabase.from('tasks').insert({
      task_id:         taskId,
      assigned_to:     primaryId,
      assigned_by:     statedAssigner?.id || profile.id,
      assignment_type: assignmentType,
      team_id:         resolvedTeam,
      title,
      urgency:         urgency || 'Med',
      due_date:        dueDate || null,
      who_due_to:      whoTo || null,
      notes:           notes || null,
      icon:            icon || null,
      date_assigned:   new Date().toISOString(),
      status:          'Not Started',
      parent_task_id:  parentTaskId || null,
      project_id:        projectId || null,
      project_column_id: projectColumnId || null,
      project_pos:       projectPos ?? null,
    }).select().single()

    if (error) return { ok: false, msg: error.message }

    // Insert all assignees into junction table — must await so data exists before real-time refetch
    if (data) {
      const rows = ids.map((id, i) => ({
        task_id: data.id,
        profile_id: id,
        is_primary: i === 0,
      }))
      const { error: jErr } = await supabase.from('task_assignees').insert(rows)
      if (jErr) console.warn('task_assignees insert failed:', jErr.message)

      // Sender-side "poke": broadcast a task-changed event to each assignee's
      // private channel. This is the most reliable path for the recipient —
      // it bypasses the tasks+task_assignees RLS/replica-identity maze that
      // can cause postgres_changes events to be dropped for secondary
      // assignees. Fire-and-forget; each assignee's useTasks hook is
      // subscribed to user:{id}:task-poke.
      for (const id of ids) {
        if (id === profile?.id) continue // no need to poke self
        pokeAssignee(id).catch(() => { /* noop */ })
      }
    }

    // Log assigner override to audit log
    if (overrideAssignerId && overrideAssignerId !== profile.id && data) {
      await supabase.from('task_audit_log').insert({
        task_id:      data.id,
        event_type:   'assigner_override',
        performed_by: actualAssigner.id,
        old_value:    actualAssigner.full_name,
        new_value:    statedAssigner?.full_name,
        note:         `Entered by ${actualAssigner.full_name} on behalf of ${statedAssigner?.full_name}`
      })
    }

    return { ok: true, task: data, taskId, assignmentType }
  }

  async function updateTask(taskId, updates) {
    const { error } = await supabase
      .from('tasks')
      .update(updates)
      .eq('id', taskId)
    if (error) return { ok: false, msg: error.message }
    return { ok: true }
  }

  async function addComment(taskId, content, mentionedIds = []) {
    const { data, error } = await supabase.from('comments').insert({
      task_id:   taskId,
      author_id: profile.id,
      content,
      mentioned_ids: Array.isArray(mentionedIds) ? mentionedIds : [],
    }).select('*, author:profiles(full_name, avatar_url)').single()

    if (error) return { ok: false, msg: error.message }
    return { ok: true, comment: data }
  }

  async function getTaskComments(taskId) {
    const { data, error } = await supabase
      .from('comments')
      .select('*, author:profiles(full_name, avatar_url)')
      .eq('task_id', taskId)
      .order('created_at', { ascending: false })
    if (error) return []
    return data || []
  }

  async function acceptTask(taskId) {
    const { error } = await supabase
      .from('tasks')
      .update({ acceptance_status: 'Accepted', accepted_at: new Date().toISOString() })
      .eq('id', taskId)
    if (error) return { ok: false, msg: error.message }
    return { ok: true }
  }

  async function declineTask(taskId, reason) {
    const { error } = await supabase
      .from('tasks')
      .update({
        acceptance_status: 'Declined',
        decline_reason: reason || null,
        declined_at: new Date().toISOString()
      })
      .eq('id', taskId)
    if (error) return { ok: false, msg: error.message }
    return { ok: true }
  }

  async function reassignTask(taskId, newAssigneeId) {
    // Resolve the OLD primary assignee before we overwrite it. Everything
    // below hinges on this — if we can't read the current row (RLS, missing
    // id, etc.) we bail out rather than silently skipping cleanup.
    const { data: current, error: fetchErr } = await supabase
      .from('tasks')
      .select('id, assigned_to')
      .eq('id', taskId)
      .maybeSingle()
    if (fetchErr) return { ok: false, msg: fetchErr.message }
    if (!current) return { ok: false, msg: 'Task not found' }

    const oldAssigneeId = current.assigned_to

    // No-op reassignment — nothing to do. Don't churn chat/assignee rows.
    if (oldAssigneeId === newAssigneeId) return { ok: true }

    // 1) Remove the OLD primary's junction row. Do this before the task
    //    UPDATE so any downstream trigger on tasks sees a clean slate.
    if (oldAssigneeId) {
      const { error: delErr } = await supabase
        .from('task_assignees')
        .delete()
        .eq('task_id', taskId)
        .eq('profile_id', oldAssigneeId)
        .eq('is_primary', true)
      if (delErr) return { ok: false, msg: delErr.message }
    }

    // 2) Flip the task to the new primary + reset acceptance state. Also
    //    clear email_alert_sent so the new assignee can receive red alerts.
    const { error: updErr } = await supabase
      .from('tasks')
      .update({
        assigned_to: newAssigneeId,
        acceptance_status: 'Pending',
        decline_reason: null,
        accepted_at: null,
        declined_at: null,
        email_alert_sent: false,
      })
      .eq('id', taskId)
    if (updErr) return { ok: false, msg: updErr.message }

    // 3) Insert the NEW primary's junction row. Upsert in case the new
    //    assignee was already a secondary — we promote them to primary.
    const { error: insErr } = await supabase
      .from('task_assignees')
      .upsert(
        { task_id: taskId, profile_id: newAssigneeId, is_primary: true },
        { onConflict: 'task_id,profile_id' }
      )
    if (insErr) return { ok: false, msg: insErr.message }

    // 4) Best-effort: remove the OLD assignee from the task's chat. The
    //    sync_task_chat_participant trigger (migration 046) enrols the new
    //    one automatically; it does NOT remove stale participants.
    if (oldAssigneeId) {
      try {
        const { data: conv } = await supabase
          .from('conversations')
          .select('id')
          .eq('kind', 'task')
          .eq('task_id', taskId)
          .maybeSingle()
        if (conv) {
          await supabase
            .from('conversation_participants')
            .delete()
            .eq('conversation_id', conv.id)
            .eq('user_id', oldAssigneeId)
        }
      } catch (e) {
        // Chat cleanup failing shouldn't fail the reassignment.
        console.warn('reassignTask: chat participant cleanup failed:', e)
      }
    }

    return { ok: true }
  }

  async function deleteTask(taskId) {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId)
    if (error) return { ok: false, msg: error.message }
    return { ok: true }
  }

  async function deleteTasks(taskIds) {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .in('id', taskIds)
    if (error) return { ok: false, msg: error.message }
    return { ok: true }
  }

  async function updateTasks(taskIds, updates) {
    const { error } = await supabase
      .from('tasks')
      .update(updates)
      .in('id', taskIds)
    if (error) return { ok: false, msg: error.message }
    return { ok: true }
  }

  async function archiveTasks(taskIds) {
    if (!profile?.id || !taskIds?.length) return { ok: true }
    const rows = taskIds.map(id => ({ user_id: profile.id, task_id: id }))
    // Idempotent: re-archiving an already-archived task is a no-op (composite
    // PK + ignoreDuplicates), so bulk-archiving a mixed selection is safe.
    const { error } = await supabase
      .from('task_archives')
      .upsert(rows, { onConflict: 'user_id,task_id', ignoreDuplicates: true })
    if (error) return { ok: false, msg: error.message }
    return { ok: true }
  }

  async function unarchiveTasks(taskIds) {
    if (!profile?.id || !taskIds?.length) return { ok: true }
    const { error } = await supabase
      .from('task_archives')
      .delete()
      .eq('user_id', profile.id)
      .in('task_id', taskIds)
    if (error) return { ok: false, msg: error.message }
    return { ok: true }
  }

  async function addAssignee(taskId, profileId) {
    const { error } = await supabase.from('task_assignees').insert({
      task_id: taskId,
      profile_id: profileId,
      is_primary: false,
    })
    if (error) return { ok: false, msg: error.message }
    return { ok: true }
  }

  async function removeAssignee(taskId, profileId) {
    const { error } = await supabase.from('task_assignees')
      .delete()
      .eq('task_id', taskId)
      .eq('profile_id', profileId)
    if (error) return { ok: false, msg: error.message }
    return { ok: true }
  }

  return { assignTask, updateTask, addComment, getTaskComments, acceptTask, declineTask, reassignTask, deleteTask, deleteTasks, updateTasks, archiveTasks, unarchiveTasks, addAssignee, removeAssignee }
}

// Shared profile/team state. Same singleton pattern as TasksContext —
// the heavy `profiles` SELECT (with profile_teams + teams joins) used to
// fire once per useProfiles() consumer. With 13+ consumers across the app
// (TaskDetailPanel, ChatWidget, AssignTaskPage, NotificationBell, etc.)
// that was 13+ identical fetches on cold load. Lifting to context fires
// the SELECT once.
const ProfilesContext = createContext(null)

function useProfilesImpl() {
  const [profiles, setProfiles] = useState([])
  const [teams,    setTeams]    = useState([])
  const [loading,  setLoading]  = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      const [{ data: pData }, { data: tData }] = await Promise.all([
        supabase.from('profiles').select('*, teams!profiles_team_id_fkey(id, name), profile_teams!profile_teams_profile_id_fkey(team_id, is_primary, role, team:teams!profile_teams_team_id_fkey(id, name))').order('full_name'),
        supabase.from('teams').select('*').order('name')
      ])
      if (cancelled) return
      const profileList = pData || []
      const profileMap = Object.fromEntries(profileList.map(p => [p.id, p]))
      const enriched = profileList.map(p => {
        const pt = p.profile_teams || []
        return {
          ...p,
          team_ids: pt.length > 0 ? pt.map(r => r.team_id) : (p.team_id ? [p.team_id] : []),
          all_teams: pt.length > 0 ? pt.map(r => ({ ...r.team, is_primary: r.is_primary, role: r.role })) : (p.teams ? [{ ...p.teams, is_primary: true }] : []),
          team_roles: pt.length > 0 ? Object.fromEntries(pt.map(r => [r.team_id, r.role])) : {},
          manager: p.reports_to ? { id: p.reports_to, full_name: profileMap[p.reports_to]?.full_name } : null
        }
      })
      setProfiles(enriched)
      setTeams(tData || [])
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [])

  return useMemo(() => ({ profiles, teams, loading }), [profiles, teams, loading])
}

export function ProfilesProvider({ children }) {
  const value = useProfilesImpl()
  return <ProfilesContext.Provider value={value}>{children}</ProfilesContext.Provider>
}

export function useProfiles({ excludeExternals = false } = {}) {
  const ctx = useContext(ProfilesContext)
  // Filter is applied per-consumer (cheap memoized filter) so the singleton
  // store doesn't have to maintain two parallel lists.
  return useMemo(() => {
    if (!ctx) return { profiles: [], teams: [], loading: false }
    if (!excludeExternals) return ctx
    return {
      profiles: ctx.profiles.filter(p => p.role !== 'Agent' && p.role !== 'Client'),
      teams: ctx.teams,
      loading: ctx.loading,
    }
  }, [ctx, excludeExternals])
}
